import { UserCertifyType } from 'common/constants';
import {
  ArtWorkType,
  CooperationTagUser,
  QualificationStatus,
  RequirementType,
  RiskTagUser,
  WorkStyleType,
} from './artist';
import { GameInfo } from './game';
import { MerchantInfo } from './merchant';

// 平台用户基础数据（创作者、社媒用户等）
export interface BaseUser extends UserIdentity {
  nickName?: string;
  mediaNickname?: string;
  avatar?: string;

  id: string;
  idNumber: string;
  name: string;
  fullName?: string;
  phone: string;
  email: string;

  taxRate?: string;
  userRate?: string;
}

export type User = BaseUser;

export interface WorkshopUser {
  // 粉丝数量
  fanCount?: number;

  // 上架工坊数量
  listWorkshopCount?: number;

  // 运营工坊数量
  runningWorkshopCount?: number;

  // 运营工坊收藏数量
  runningWorkshopCollectCount?: number;

  // 评论数量
  commentCount?: number;
}

// 风险标签功能结构（v0.7.1.6 补充 RiskTagUser）
export interface Artist extends BaseUser, RiskTagUser, CooperationTagUser, WorkshopUser {
  score: number; // 评分
  requirementType: string[];
  style: string[];
  finishTaskNum: string;
  finishTaskRate: string;
  onTimeRate: string;
  artistQualificationStatus: QualificationStatus;
  artistQualificationCreateTime: string;
  userCertifyType: UserCertifyType;
  userCode?: string;
  artworks?: ArtWorkType[];
}

export interface UserAuth extends BaseUser {
  createTime: string;
  isAbroad: boolean;
  certifyStatus: QualificationStatus;
}

export enum MobileCode {
  Login = 1,
  Unbind = 2,
  Logoff = 3,
  Bind = 4,
  Register = 5,
  PersonInfoCollect = 6,
  SetPassword = 7,
}
export enum VerCodeSendStatus {
  Success,
  Failed,
  TooFrequent,
}
export interface LoginInterface {
  phone: string;
  code?: string;
  region?: string;
  scope?: number;
}

export interface RealNameAuthDetail {
  realName: string;
  cardNo: string;
  createTime: string;
  cardType: string;
  status?: QualificationStatus;
  reason?: string; // 拒绝原因
  birthday?: string;
}

export enum EnterpriseAuthStatus {
  Uncertified = 10,
  Waiting = 20,
  Failed = 30,
  Verified = 40,
}

// TODO: 需要将 packages/manager/src/types/enterpriseAuth.ts 提取公共后合并
export interface EnterpriseAuthDetail {
  enterpriseName: string; //企业名称
  enterpriseLegalPersonName: string; //企业法人
  taxpayerId?: string; //纳税人识别号
  accountName?: string; //开户行名称
  bankNo?: string; // 开户支行代号
  bankCardNo?: string; // 银行卡号
  name?: string; //联系人姓名
  phone?: string; //联系人电话
  mail?: string; //联系人邮箱
  mandateFile?: { url: string; name: string } | File; //委托书
  licenseFile?: { url: string; name: string } | File; // 营业执照
  applyTime?: string; //认证通过时间
  registerAddress?: string; //注册地址
  status?: EnterpriseAuthStatus; //认证状态
  reason?: string; // 拒绝原因
  remainEnterpriseAuthCount?: number; // 剩余打款次数
  bankId?: string; // 银行ID
}

export interface AuthInfo {
  realNameInfo: RealNameAuthDetail;
  enterpriseInfo: EnterpriseAuthDetail;
  userCertifyType: UserCertifyType;
  realNameAuthenticated: boolean;
  enterpriseAuthenticated: boolean;
}

//个人支付信息
export interface PersonalPayInfo {
  alipayNo?: string;
  bankCardNo?: string;
  // bankBranch?: string; // 开户支行
  bankAccountName?: string; // 开户名称
  bankSwiftCode?: string; // 银行国际代码
  realName?: string; // 真实姓名
  merchantInfo?: MerchantInfo; // 商户信息
}

export interface RoleItemProps {
  roleName: string;
  roleCode: string;
  roleType: string;
}

export type userRoleItemType = string | RoleItemProps;

export interface UserIdentity {
  uniqueId?: number; // 认证创作者id
  mediaUserId?: number; // 社媒用户id
  userCode?: string; // 新版本用户唯一识别（原 uniqueId 向此靠齐）
  userId?: number; //用户ID
}

// 账号基础信息
export interface AccountType extends BaseUser {
  score: number; // 积分
  level: number; // 等级
  realNameAuthenticated: boolean;
  enterpriseAuthenticated: boolean;
  createTime: string;
  roleType: number;
  csUserId: number;
  userCertifyType?: UserCertifyType;
  realNameAuthDetail: RealNameAuthDetail;
  enterpriseAuthDetail: EnterpriseAuthDetail;
  platformRole?: Array<RoleItemProps>;
  taxRate: string;
  userRate: string;
  externalConcatAmount?: string; // 制作方到账金额
  qualificationId?: string; //创作者认证资质ID
  artistQualificationStatus?: QualificationStatus; //创作者认证状态
  bankCardNo?: string; //银行卡号
  company?: string; //公司名称
  region?: string; //地区
  personalProfile?: string;
  isPlatformAdmin?: boolean;
  isAdmin?: boolean;
  isAbroad?: boolean; // 是否海外用户账号
  isDeveloper?: boolean;
  changeNickNameLimit?: number;
  changeNickNameTotal?: number;
  changeNickNameCards?: number; // 改名卡
  corpEmail?: string; // 企业邮箱
}

/**
 * 易盾身份证认证错误原因
 * https://support.dun.163.com/documents/391676076156063744?docId=391677747099668480
 */
export enum CardError {
  NameConflicting = 2,
  NotFound = 3,
  PicConflicting = 4,
  Failure = 7,
}

export interface PersonalVerifyResult {
  verify: Boolean;
  msg?: string;
  reasonType?: string;
}

export const CardErrorReason: Record<CardError, string> = {
  [CardError.NameConflicting]: '姓名与证件号或登录手机号实名信息不匹配',
  [CardError.NotFound]: '查无此身份证',
  [CardError.PicConflicting]: '身份证照片信息与输入信息不一致',
  [CardError.Failure]: '结果获取失败，请重试',
};

export interface EnterpriseDetail {
  mail: string;
  accountTitle: string;
  updateTime: number;
  bankName: string;
  userId: number;
  bankcardNo: string;
  registerAddress: string;
  mandateFile: string;
  createTime: number;
  phone: string;
  name: string;
  validTime: number;
  id: number;
  enterpriseName: string;
  licenseFile: string;
  taxpayerId: string;
}

export enum ScheduleBusyStatus {
  Free = 'FREE',
  Busy = 'BUSY',
}

export interface Schedule {
  month: number;
  year: number;
  //上半月
  upperHalf: ScheduleBusyStatus;
  laterHalf: ScheduleBusyStatus;
}

export enum CommentLevelEnum {
  Good = 'GOOD',
  Normal = 'NORMAL',
  Bad = 'BAD',
}

export type Comment = {
  uniqueId: number;
  avatar?: string;
  content: string;
  nickname: string;
  requirementType: string;
  score: number;
  styles: WorkStyleType[];
  createTime: string;
  isMe: boolean;
  tags: {
    dimension: string;
    id: number;
    name: string;
  }[];
  level: CommentLevelEnum;
};

export type UserHomePageDetail = {
  artistQualificationStatus: QualificationStatus;
  artwork: ArtWorkType[];
  workRequirementTypes: { id: number; name: string }[];
  artworkTotal: number;
  avatar: string | null;
  isMe: boolean;
  nickName: string;
  personalProfile: string | null;
  requirementType: RequirementType[];
  uniqueId: number;
  userCertifyType: UserCertifyType;
  schedules: Schedule[];
  comments: Comment[];
  commentsTotal: number;
  avgScore?: number;
  isConcerned?: boolean; // 是否已关注该用户
  relationGames?: GameInfo[];
};

export type AccountAmountInfo = {
  chargeCreditLimitTotal: number;
  chargeAmount: number;
  chargeCreditLimitBalance: number;
  incomeScore: number;
  withdrawLimit: number;
  monthWithdrawAmount: number;
  monthWithdrawLeft: number;
  isFreeze: boolean;
  alipayNo?: string;
  bankCardNo?: string;
  age?: number;
  enterpriseInfo: {
    bankCardNo?: string;
    bankName?: string;
    accountName?: string;
  };
  //0.7.9新增
  taskBConsume: number; // 任务B支出
  taskBIncome: number; // 任务B收入
  workshopConsume: number; // 工坊支出
  workshopIncome: number; // 工坊收入
  accountWithdrewScore: number; // 账户已提现积分,单位是积分
  totalTax: number; // 累计缴税金额,单位元
};

// 绑定邮箱传入4，换绑的时候老邮箱传入2，新邮箱传入4
export enum EmailAuthCodeScopeEnum {
  Bind = 4,
  ChangeBindOld = 2,
  ChangeBindNew = 4,
  Logoff = 3,
}

export enum RecommendType {
  Common,
  YL,
}
